/**
 * Shared Swiper carousel — <product-carousel>
 *
 * Configure via data attributes on the custom element. Queries are scoped to `this`.
 *
 * data-swiper          Selector for Swiper root (default: .swiper)
 * data-next            Next button selector (default: .swiper-btn-next, fallback .swiper-button-next)
 * data-prev            Prev button selector (default: .swiper-btn-prev, fallback .swiper-button-prev)
 * data-scrollbar       Scrollbar selector, or "false" to disable (default: .swiper-scrollbar)
 * data-pagination      Pagination selector, or "false" to disable (default: off)
 * data-pagination-type Pagination type (default: bullets). e.g. progressbar
 * data-nav             "true" | "false" — force nav on/off (default: auto if buttons exist)
 * data-slides-per-view Number or "auto" (default: 1.5)
 * data-slides-per-group Number (default: 1)
 * data-space-between   Number (default: 16)
 * data-loop            "true" | "false"
 * data-autoplay        Delay ms, or "false"/empty to disable
 * data-auto-height     "true" | "false" (default: false)
 * data-speed           Transition speed ms
 * data-observer        "true" | "false" (default: true)
 * data-effect          Swiper effect (default: slide). e.g. fade
 * data-breakpoints     JSON object of Swiper min-width breakpoints
 *
 * Example:
 * <product-carousel
 *   data-swiper=".swiper_container"
 *   data-nav="true"
 *   data-scrollbar=".swiper-scrollbar"
 *   data-slides-per-view="1.5"
 *   data-breakpoints='{"750":{"slidesPerView":2.5},"990":{"slidesPerView":3}}'
 * >
 */
if (!customElements.get('product-carousel')) {
  class ProductCarousel extends HTMLElement {
    /** @type {number} */
    _tries = 0;

    /** @type {(() => void) | null} */
    _onResize = null;

    /** @type {SwiperInstance | null} */
    swiper = null;

    connectedCallback() {
      this._tries = 0;
      this._applyPrelayout();
      this._onResize = () => this._applyPrelayout();
      window.addEventListener('resize', this._onResize, { passive: true });
      this._initWhenReady();
    }

    disconnectedCallback() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      this.swiper?.destroy?.(true, true);
      this.swiper = null;
    }

    _applyPrelayout() {
      const baseSpv = this._parseNumber(this.dataset.slidesPerView, 1.5);
      const baseSpace = this._parseNumber(this.dataset.spaceBetween, 16);
      let slidesPerView = baseSpv;
      let spaceBetween = baseSpace;

      const breakpoints = this._parseBreakpoints(this.dataset.breakpoints);
      if (breakpoints) {
        const width = window.innerWidth;
        const keys = Object.keys(breakpoints)
          .map(Number)
          .filter((n) => Number.isFinite(n))
          .sort((a, b) => a - b);

        for (const bp of keys) {
          if (width < bp) continue;
          const cfg = breakpoints[bp] || breakpoints[String(bp)];
          if (!cfg || typeof cfg !== 'object') continue;
          if (cfg.slidesPerView != null) {
            slidesPerView = this._parseNumber(cfg.slidesPerView, slidesPerView);
          }
          if (cfg.spaceBetween != null) {
            spaceBetween = this._parseNumber(cfg.spaceBetween, spaceBetween);
          }
        }
      }

      if (slidesPerView === 'auto' || !Number.isFinite(Number(slidesPerView))) {
        slidesPerView = baseSpv === 'auto' ? 1.5 : baseSpv;
      }

      this.style.setProperty('--pc-slides-per-view', String(slidesPerView));
      this.style.setProperty('--pc-space-between', `${spaceBetween}px`);
    }

    _initWhenReady() {
      if (window.Swiper) {
        this._initSwiper();
        return;
      }

      this._tries += 1;
      if (this._tries > 80) return;
      // Prefer rAF while waiting; fall back to short timeout
      if (this._tries < 10) {
        requestAnimationFrame(() => this._initWhenReady());
      } else {
        window.setTimeout(() => this._initWhenReady(), 25);
      }
    }

    /**
     * @param {unknown} value
     * @param {number | 'auto' | undefined} fallback
     * @returns {number | 'auto' | undefined}
     */
    _parseNumber(value, fallback) {
      if (value == null || value === '') return fallback;
      if (value === 'auto') return 'auto';
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    }

    /**
     * @param {string | null | undefined} value
     * @param {boolean} fallback
     * @returns {boolean}
     */
    _parseBool(value, fallback) {
      if (value == null || value === '') return fallback;
      return value === 'true' || value === '1';
    }

    /**
     * @param {string | undefined} raw
     * @returns {Record<string, Record<string, unknown>> | undefined}
     */
    _parseBreakpoints(raw) {
      if (!raw) return undefined;
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : undefined;
      } catch (e) {
        console.warn('[product-carousel] invalid data-breakpoints JSON', e);
        return undefined;
      }
    }

    /**
     * @param {(string | null | undefined)[]} selectors
     * @returns {Element | null}
     */
    _queryFirst(selectors) {
      for (const sel of selectors) {
        if (!sel || sel === 'false') continue;
        const el = this.querySelector(sel);
        if (el) return el;
      }
      return null;
    }

    _buildOptions() {
      const slidesPerView = this._parseNumber(this.dataset.slidesPerView, 1.5);
      const slidesPerGroup = this._parseNumber(this.dataset.slidesPerGroup, 1);
      const spaceBetween = this._parseNumber(this.dataset.spaceBetween, 16);
      const speed = this._parseNumber(this.dataset.speed, undefined);
      const loop = this._parseBool(this.dataset.loop, false);
      const useObserver = this._parseBool(this.dataset.observer, true);
      const breakpoints = this._parseBreakpoints(this.dataset.breakpoints);

      /** @type {Record<string, unknown>} */
      const options = {
        slidesPerView,
        slidesPerGroup,
        spaceBetween,
        loop,
        observer: useObserver,
        observeParents: useObserver,
        observeSlideChildren: useObserver,
      };

      if (speed != null) options.speed = speed;
      if (breakpoints) options.breakpoints = breakpoints;
      if (this._parseBool(this.dataset.autoHeight, false)) {
        options.autoHeight = true;
      }

      const effect = this.dataset.effect;
      if (effect && effect !== 'slide') {
        options.effect = effect;
        if (effect === 'fade') {
          options.fadeEffect = { crossFade: true };
        }
      }

      const autoplayRaw = this.dataset.autoplay;
      if (autoplayRaw && autoplayRaw !== 'false') {
        const delay = this._parseNumber(autoplayRaw, 3000);
        options.autoplay = {
          delay,
          disableOnInteraction: false,
        };
      }

      const navForced = this.dataset.nav;
      const nextEl = this._queryFirst([
        this.dataset.next,
        '.swiper-btn-next',
        '.swiper-button-next',
      ]);
      const prevEl = this._queryFirst([
        this.dataset.prev,
        '.swiper-btn-prev',
        '.swiper-button-prev',
      ]);
      const enableNav =
        navForced === 'false'
          ? false
          : navForced === 'true'
            ? true
            : Boolean(nextEl || prevEl);

      if (enableNav && (nextEl || prevEl)) {
        options.navigation = {
          nextEl: nextEl || null,
          prevEl: prevEl || null,
        };
      }

      const scrollbarRaw = this.dataset.scrollbar;
      if (scrollbarRaw !== 'false') {
        const scrollbarEl = this._queryFirst([
          scrollbarRaw && scrollbarRaw !== 'true' ? scrollbarRaw : null,
          '.swiper-scrollbar',
        ]);
        if (scrollbarEl) {
          options.scrollbar = {
            el: scrollbarEl,
            draggable: true,
          };
        }
      }

      const paginationRaw = this.dataset.pagination;
      if (paginationRaw && paginationRaw !== 'false') {
        const paginationEl = this._queryFirst([
          paginationRaw !== 'true' ? paginationRaw : null,
          '.swiper-pagination',
        ]);
        if (paginationEl) {
          options.pagination = {
            el: paginationEl,
            type: this.dataset.paginationType || 'bullets',
            clickable: true,
          };
        }
      }

      return options;
    }

    _initSwiper() {
      const SwiperCtor = window.Swiper;
      if (this.swiper || !SwiperCtor) return;

      const swiperSelector = this.dataset.swiper || '.swiper';
      const container = this.querySelector(swiperSelector);
      if (!container) return;

      this._applyPrelayout();
      this.swiper = new SwiperCtor(container, this._buildOptions());
    }
  }

  customElements.define('product-carousel', ProductCarousel);
}
